import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { PageTopControls } from '@/components/PageTopControls';
import { IconBox } from '@/components/UI/IconBox/IconBox.styles';
import * as S from '@/views/Error/Error.styles';
import { ROUTES } from '@/constants/routes';
import { CommonTestIds } from '@/__e2e__/testIds/common';
import { useBanner } from '@/hooks/useBanner';
import { Header } from '@/components/Header';
import { useResponsive } from '@/hooks/useResponsive';
import { DesktopFooter, Footer } from '@/components/Footer';

interface ErrorViewProps {
  title?: string;
  subtitle?: string;
  statusCode?: number;
  buttonText?: string;
  onClick?: () => void;
  hasTopBanner?: boolean;
  hasFooter?: boolean;
}

export const ErrorView: React.FC<ErrorViewProps> = ({
  title,
  subtitle,
  statusCode,
  buttonText,
  onClick,
  hasTopBanner,
  hasFooter,
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { topBanner } = useBanner();
  const { isDesktop } = useResponsive();

  const isUnexpectedError = statusCode === 500;
  const isNotFound = statusCode === 404;
  const imageFilename = isUnexpectedError
    ? 'unexpected-error.png'
    : 'not-found.png';
  const finalTitle =
    title ??
    t(isNotFound ? 'pages.error.not_found' : 'pages.error.unexpected_error');
  const finalSubtitle =
    subtitle ??
    t(
      isNotFound
        ? 'pages.error.not_found_subtitle'
        : 'pages.error.unexpected_error_subtitle'
    );
  const finalButtonText =
    buttonText ??
    t(isNotFound ? 'pages.error.go_to_main' : 'pages.error.try_again');

  function onButtonClick() {
    if (onClick) return onClick();
    router.replace(ROUTES.home);
  }

  function renderFooter() {
    if (isDesktop) {
      return <DesktopFooter />;
    }
    return <Footer />;
  }

  return (
    <>
      {hasTopBanner && topBanner}

      <PageTopControls>
        <Header isMain />
      </PageTopControls>

      <S.Box $isFooterPresented={hasFooter}>
        <S.Container>
          <IconBox
            $filename={imageFilename}
            $width={255}
            $height={218}
            data-testid={
              isUnexpectedError
                ? CommonTestIds.errorPage.unexpectedErrorImage
                : CommonTestIds.errorPage.notFoundImage
            }
          />

          <S.Title data-testid={CommonTestIds.errorPage.title}>
            {finalTitle}
          </S.Title>

          <S.Text data-testid={CommonTestIds.errorPage.subtitle}>
            {finalSubtitle}
          </S.Text>

          {(isNotFound || isUnexpectedError || (buttonText && onClick)) && (
            <S.Action
              appearance="secondary"
              isFullWidth
              onClick={onButtonClick}
            >
              {finalButtonText}
            </S.Action>
          )}
        </S.Container>
      </S.Box>

      {hasFooter && renderFooter()}
    </>
  );
};
